import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import CookieConsentContext from 'web/components/CookieConsentContext';
import { shouldUseEmulators } from 'web/components/FirebaseContext/common';
import {
  deferredMeasurementDelayMs,
  getMeasurementConsent,
  shouldDeferMeasurementOnPath,
  type IubendaPreferences,
} from './cookieConsent';
import './cookieBanner.css';

type IubendaApi = {
  getPreferences?: () => IubendaPreferences;
  openPreferences?: () => void;
};

type IubendaGlobal = unknown[] & {
  cs?: {
    api?: IubendaApi;
  };
  csConfiguration?: Record<string, unknown>;
  csLangConfiguration?: Record<string, unknown>;
};

declare global {
  interface Window {
    _iub?: IubendaGlobal;
  }
}

const loadScript = (id: string, src: string, async: boolean) =>
  new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(id) as HTMLScriptElement | null;
    if (existingScript?.dataset.loaded === 'true') {
      resolve();
      return;
    }

    const script = existingScript || document.createElement('script');
    const onLoad = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });

    if (!existingScript) {
      script.id = id;
      script.src = src;
      script.type = 'text/javascript';
      script.async = async;
      document.head.appendChild(script);
    }
  });
const ignoreScriptLoadError = (): void => undefined;

const CookieBanner = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const shouldDeferMeasurement = shouldDeferMeasurementOnPath(pathname);
  const [measurementConsent, setMeasurementConsent] = useState(false);
  const [measurementReady, setMeasurementReady] = useState(!shouldDeferMeasurement);
  const updateMeasurementConsent = useCallback((preferences?: IubendaPreferences, fallback = false) => {
    setMeasurementConsent(getMeasurementConsent(preferences) ?? fallback);
  }, []);

  useEffect(() => {
    if (!shouldDeferMeasurement || measurementReady) {
      return;
    }

    let timer: number | undefined;
    const enableMeasurement = () => setMeasurementReady(true);
    const scheduleFallback = () => {
      timer = window.setTimeout(enableMeasurement, deferredMeasurementDelayMs);
    };

    // Once interaction enables measurement, keep it enabled across client-side navigation for this provider lifetime.
    document.addEventListener('pointerdown', enableMeasurement, { passive: true, once: true });
    document.addEventListener('keydown', enableMeasurement, { once: true });
    if (document.readyState === 'complete') {
      scheduleFallback();
    } else {
      window.addEventListener('load', scheduleFallback, { once: true });
    }

    return () => {
      document.removeEventListener('pointerdown', enableMeasurement);
      document.removeEventListener('keydown', enableMeasurement);
      window.removeEventListener('load', scheduleFallback);
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [measurementReady, shouldDeferMeasurement]);

  useEffect(() => {
    if (!shouldDeferMeasurement) {
      setMeasurementReady(true);
    }
  }, [shouldDeferMeasurement]);

  useEffect(() => {
    if (shouldUseEmulators) {
      return;
    }

    let active = true;
    const updateConsent = (preferences?: IubendaPreferences, fallback = false, userExpressedPreference = false) => {
      if (active) {
        if (userExpressedPreference) {
          setMeasurementReady(true);
        }
        updateMeasurementConsent(preferences, fallback);
      }
    };
    const getStoredPreferences = () => window._iub?.cs?.api?.getPreferences?.();

    window._iub = window._iub || [];
    window._iub.csConfiguration = {
      applyGdprForCH: false,
      askConsentAtCookiePolicyUpdate: true,
      countryDetection: true,
      emailMarketing: {
        theme: 'dark',
      },
      enableFadp: true,
      enableLgpd: true,
      enableUspr: true,
      floatingPreferencesButtonDisplay: false,
      gdprAppliesGlobally: false,
      lgpdAppliesGlobally: false,
      perPurposeConsent: true,
      siteId: 1365907,
      storage: {
        useSiteId: true,
      },
      usPreferencesWidgetDisplay: false,
      whitelabel: false,
      cookiePolicyId: 54070999,
      banner: {
        acceptButtonDisplay: true,
        closeButtonDisplay: false,
        customizeButtonDisplay: true,
        explicitWithdrawal: true,
        listPurposes: true,
        ownerName: 'Introwise',
        position: 'float-top-center',
        rejectButtonDisplay: true,
        showPurposesToggles: true,
        showTitle: false,
        showTotalNumberOfProviders: true,
      },
      callback: {
        onReady: (consent: boolean) => updateConsent(getStoredPreferences(), consent),
        onPreferenceExpressed: (preferences: IubendaPreferences) => updateConsent(preferences, false, true),
        onPreferenceNotNeeded: () => updateConsent(undefined, true),
        onPreferenceChange: (_previous: IubendaPreferences, current: IubendaPreferences) =>
          updateConsent(current, false, true),
        onStartupFailed: () => updateConsent(),
      },
    };
    window._iub.csLangConfiguration = {
      en: {
        cookiePolicyId: 54070999,
      },
    };

    const storedPreferences = getStoredPreferences();
    if (storedPreferences) {
      updateConsent(storedPreferences);
    }

    const loadIubenda = async () => {
      await loadScript('iubenda-cs-sync', 'https://cs.iubenda.com/sync/1365907.js', false).catch(ignoreScriptLoadError);
      if (!active) return;
      await loadScript('iubenda-gpp-stub', 'https://cdn.iubenda.com/cs/gpp/stub.js', false).catch(
        ignoreScriptLoadError,
      );
      if (!active) return;
      try {
        await loadScript('iubenda-cs', 'https://cdn.iubenda.com/cs/iubenda_cs.js', true);
        const loadedPreferences = getStoredPreferences();
        if (loadedPreferences) {
          updateConsent(loadedPreferences);
        }
      } catch {
        updateConsent();
      }
    };
    void loadIubenda();

    return () => {
      active = false;
    };
  }, [updateMeasurementConsent]);

  const measurementAllowed = measurementConsent && measurementReady;
  const contextValue = useMemo(() => ({ measurementAllowed }), [measurementAllowed]);
  return <CookieConsentContext.Provider value={contextValue}>{children}</CookieConsentContext.Provider>;
};

export default CookieBanner;
